import C from "@/modules/billing/choose_method/ChooseMethod.module.css";

import { Separator } from "@/components/Separator.tsx";
import { WSwitch } from "@/components/forms/WSwitch.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import type { AddMethodProps } from "@/modules/billing/choose_method/AddMethodParams.ts";
import { StatusDisplay } from "@/modules/billing/choose_method/StatusDisplay.tsx";
import { PaymentMethodTitle } from "@/modules/billing/paymentMethodLogic.tsx";
import type { PaymentMethod, PaymentMethodId } from "@/modules/billing/paymentMethods.ts";
import { getShowCardGatewayInfo } from "@/modules/billing/utils.tsx";
import type { BaseProps } from "@/utils/baseProps.ts";
import { cn } from "@/utils/classNames.ts";
import { useControlledState } from "@/utils/react/useControlledState.tsx";
import { Trans } from "@lingui/react/macro";
import type { Atom } from "jotai/vanilla";
import { useEffect, useRef, useState } from "react";
import { useFocus } from "react-aria";

export interface ChooseMethodProps extends ChooseMethodContentProps {
    hasAutomatedSwitch?: boolean;
    defaultOnlyAutomated?: boolean;
}

export function ChooseMethod({
    hasAutomatedSwitch,
    defaultOnlyAutomated = false,
    methods,
    ...props
}: ChooseMethodProps) {
    //#region Hooks
    const [onlyAutomated, setAutomated] = useState<boolean>(defaultOnlyAutomated);
    //#endregion

    methods = methods.filter((m) => !onlyAutomated || m.isAutomated);

    /* The surrounding div is needed because some modal content components use display: flex with a gap. */
    return (
        <div>
            <div className={cn("flex items-center justify-between", !hasAutomatedSwitch && "pt-2")}>
                <p>
                    <Trans>Choose method</Trans>
                </p>

                {hasAutomatedSwitch && (
                    <WSwitch isSelected={onlyAutomated} onChange={setAutomated} side="right">
                        <Trans comment="Only payment methods that can be charged automatically">
                            Only automated methods
                        </Trans>
                    </WSwitch>
                )}
            </div>

            <div className="py-3">
                <Separator />
            </div>

            <ChooseMethodContent className={C.modalList} methods={methods} {...props} />
        </div>
    );
}

export interface ChooseMethodContentProps extends BaseProps {
    methods: PaymentMethod[];
    isDisabled?: boolean;
    scrollToSelected?: boolean;

    formProps: AddMethodProps;
    statusAtom?: Atom<string | null>;

    /** `null` means nothing selected */
    defaultValue?: PaymentMethodId | null;
    value?: PaymentMethodId | null;
    onChange?: (id: PaymentMethodId) => void;
}

export interface MethodIconProps {
    isSelected: boolean;
}

export function ChooseMethodContent({
    methods,
    formProps,
    value: controlledValue,
    defaultValue = null,
    onChange,
    isDisabled = false,
    scrollToSelected = false,
    statusAtom,
    ...baseProps
}: ChooseMethodContentProps) {
    //#region Hooks
    const [value, setValue] = useControlledState(controlledValue, defaultValue, onChange);
    const selectedRef = useRef<HTMLDivElement>(null);
    const [isFocus, setFocus] = useState(false);
    //#endregion

    const { focusProps } = useFocus({
        onFocus: (event) => {
            setValue(event.target.id as PaymentMethodId);
            setFocus(true);
        },
        onBlur: () => setFocus(false),
    });

    function onClick(value: PaymentMethodId) {
        setValue(value);
    }

    // Scroll the first selected item into view on mount
    useEffect(() => {
        if (scrollToSelected)
            selectedRef.current?.scrollIntoView({
                behavior: "instant",
                block: "center",
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showCardGatewayInfo = getShowCardGatewayInfo(methods.map((m) => m.id));
    return (
        <div {...baseProps}>
            {methods.map((method) => {
                const { id, icon, AddForm, name } = method;
                const isSelected = value === id;
                const formContent = isSelected ? (
                    <>
                        <ContentLoadingSuspense>
                            <AddForm {...formProps} />
                        </ContentLoadingSuspense>
                        {statusAtom && <StatusDisplay statusAtom={statusAtom} />}
                    </>
                ) : undefined;

                return (
                    <div
                        key={id}
                        id={id}
                        {...focusProps}
                        // biome-ignore lint/a11y/noNoninteractiveTabindex: Work in progress keyboard navigation
                        tabIndex={0}
                        ref={isSelected ? selectedRef : undefined}
                        data-selected={isSelected ? true : undefined}
                        data-disabled={isDisabled ? true : undefined}
                        data-focused={isFocus ? true : undefined}
                        className={C.ListItem}
                        onClick={() => onClick(id)}
                    >
                        <div className={C.item}>
                            <div className="flex items-center justify-between">
                                <PaymentMethodTitle method={method} showGateway={showCardGatewayInfo} />
                                {icon({ isSelected })}
                            </div>
                        </div>

                        {formContent}
                    </div>
                );
            })}
        </div>
    );
}
